import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {getScoreStatLevel} from '../../../../../components/ScoreStatCard/scoreStatUtils';
import {orangeColors, redeploymentChip, scorePill} from '../../../../../layouts/theme/designSystem';
import type {
  DepartmentHealthConfidence,
  DepartmentHealthDetail,
  DepartmentHealthState,
} from '../../types/DepartmentHealthTypes';
import {
  buildDepartmentHealthNarrativeContext,
  getNarrativeSignalColors,
} from '../../utils/DepartmentHealth/buildDepartmentHealthNarrativeContext';

interface Props {
  departmentName: string;
  health: DepartmentHealthDetail;
}

const healthStateHeadlineKey: Record<DepartmentHealthState, string> = {
  well_aligned: 'wellAligned',
  functional: 'functional',
  at_risk: 'atRisk',
};

const confidenceChipColors: Record<
  DepartmentHealthConfidence,
  {bgcolor: string; color: string; borderColor: string}
> = {
  high: {bgcolor: scorePill.high.bgcolor, color: scorePill.high.color, borderColor: scorePill.high.strokeColor},
  medium: {bgcolor: scorePill.medium.bgcolor, color: scorePill.medium.color, borderColor: orangeColors[100]},
  low: {bgcolor: scorePill.low.bgcolor, color: scorePill.low.color, borderColor: scorePill.low.strokeColor},
};

const chipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  px: 1.375,
  py: 0.5,
  borderRadius: '99px',
  fontSize: '11.5px',
  fontWeight: 500,
  border: '1px solid',
} as const;

export const DepartmentHealthNarrativeHeader = ({departmentName, health}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.narrative'});
  const context = useMemo(() => buildDepartmentHealthNarrativeContext(health, health.benchmarks), [health]);
  const {signalColor} = getNarrativeSignalColors(health.narrative.healthState);
  const headlineKey = healthStateHeadlineKey[health.narrative.healthState];
  const aboveBelowLabel = t(`aboveBelow.${context.aboveBelow}`);
  const weakestDimensionLabel = context.weakestDimension ? t(`dimensionLabels.${context.weakestDimension}`) : '';

  const bodyParagraphs = useMemo(() => {
    const shared = {
      departmentName,
      deptAverage: context.deptAverage ?? '—',
      benchmarkOverall: context.benchmarkOverall ?? '—',
      aboveBelow: aboveBelowLabel,
      profileCount: context.profileCount,
      strongFitCount: context.strongFitCount,
      lowProfileCount: context.lowProfileCount,
      weakestDimension: weakestDimensionLabel,
      redeploymentCount: context.redeploymentCount,
      benchmarkDelta: context.benchmarkDelta !== null ? Math.abs(context.benchmarkDelta) : '—',
    };

    if (health.narrative.healthState === 'well_aligned') {
      const parts = [t('bodies.wellAligned.lead', shared), t('bodies.wellAligned.strongFit', shared)];
      parts.push(
        context.weakestBelowBenchmark && context.weakestDimension
          ? t('bodies.wellAligned.weakestWatch', shared)
          : t('bodies.wellAligned.allAtNorm')
      );
      return parts.join(' ');
    }

    if (health.narrative.healthState === 'functional') {
      const parts = [
        t('bodies.functional.lead', shared),
        context.lowProfileCount > 0 ? t('bodies.functional.lowProfiles', shared) : t('bodies.functional.noLowProfiles'),
        t('bodies.functional.weakest', shared),
      ];
      if (context.redeploymentCount > 0) {
        parts.push(t('bodies.functional.redeployment', shared));
      }
      return parts.join(' ');
    }

    return t('bodies.atRisk.full', shared);
  }, [aboveBelowLabel, context, departmentName, health.narrative.healthState, t, weakestDimensionLabel]);

  const avgScoreLevel = context.deptAverage !== null ? getScoreStatLevel(context.deptAverage) : null;
  const avgScorePill = avgScoreLevel ? scorePill[avgScoreLevel] : null;
  const normDeltaPositive = context.benchmarkDelta !== null && context.benchmarkDelta >= 0;
  const normDeltaChipColors = normDeltaPositive ? scorePill.high : scorePill.low;
  const confidenceColors = confidenceChipColors[health.dataFreshness.confidence];

  return (
    <EmpionPaper
      sx={{
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        px: 3.5,
        py: 3.25,
        mb: 2.5,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          bgcolor: signalColor,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: '10.5px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.7px',
          color: 'text.secondary',
          mb: 1.125,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {t('eyebrow.label')}
        <Box component='span' sx={{width: 5, height: 5, borderRadius: '50%', bgcolor: signalColor}} />
        {t('eyebrow.department', {departmentName, count: context.profileCount})}
      </Typography>

      <Typography
        component='h2'
        sx={{
          fontSize: '24px',
          lineHeight: 1.25,
          letterSpacing: '-0.2px',
          color: 'text.primary',
          mb: 1.25,
          maxWidth: 660,
        }}
      >
        {departmentName} {t(`headlines.${headlineKey}.prefix`)}{' '}
        <Box component='em' sx={{fontStyle: 'italic', color: signalColor}}>
          {t(`headlines.${headlineKey}.emphasis`)}
        </Box>
        {t(`headlines.${headlineKey}.suffix`)}
      </Typography>

      <Typography
        sx={{
          fontSize: '13px',
          color: 'text.secondary',
          lineHeight: 1.7,
          maxWidth: 660,
          mb: 2,
        }}
      >
        {bodyParagraphs}
      </Typography>

      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.875}}>
        {context.deptAverage !== null && avgScorePill && (
          <Box
            component='span'
            sx={{
              ...chipSx,
              bgcolor: avgScorePill.bgcolor,
              color: avgScorePill.color,
              border: 'none',
            }}
          >
            {t('chips.avgAlignment', {value: context.deptAverage})}
          </Box>
        )}

        {context.benchmarkDelta !== null && context.benchmarkOverall !== null ? (
          <Box
            component='span'
            sx={{
              ...chipSx,
              bgcolor: normDeltaChipColors.bgcolor,
              color: normDeltaChipColors.color,
              border: 'none',
            }}
          >
            {normDeltaPositive
              ? t('chips.aboveNorm', {delta: context.benchmarkDelta})
              : t('chips.belowNorm', {delta: context.benchmarkDelta})}
          </Box>
        ) : null}

        {context.redeploymentCount > 0 ? (
          <Box
            component='span'
            sx={{
              ...chipSx,
              bgcolor: redeploymentChip.bgcolor,
              color: redeploymentChip.color,
              borderColor: redeploymentChip.borderColor,
            }}
          >
            {context.redeploymentCount === 1
              ? t('chips.redeployment', {count: context.redeploymentCount})
              : t('chips.redeployment_plural', {count: context.redeploymentCount})}
          </Box>
        ) : null}

        <Box
          component='span'
          sx={{
            ...chipSx,
            bgcolor: confidenceColors.bgcolor,
            color: confidenceColors.color,
            borderColor: confidenceColors.borderColor,
          }}
        >
          {t(`confidence.${health.dataFreshness.confidence}`)}
        </Box>
      </Box>
    </EmpionPaper>
  );
};
